import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import MarketChart from '../../components/UIComponents/MarketChart';
import { v2GetRates, v2GetRatesHistory, v2GetXStock } from '../../services/v2/api';
import { getFavoriteTokenSymbols, toggleFavoriteTokenSymbol } from '../../services/tokenFavorites';
import type { V2RateHistoryPeriod, V2RateHistoryPoint, V2RateInfo, V2Token, V2XStock } from '../../types/v2';
import { COLORS } from '../../theme/themeTokens';
import { calculatePeriodChange } from '../../utils/marketChart';
import { priceAgeNote, tradingPausedReason, xStockTicker } from '../../utils/xstocks';

const PERIODS: { value: V2RateHistoryPeriod; label: string }[] = [
  { value: '1d', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '1y', label: '1Y' },
];

type TokenDetailScreenProps = {
  onBack: () => void;
  onNavigate: (screen: string) => void;
} & (
  // Buy and Sell open the exchange form with the token or stock filled in.
  | { token: V2Token; stock?: undefined; onBuy: (token: V2Token) => void; onSell: (token: V2Token) => void }
  // An xStock uses the same screen (design "Amazon xStocks").
  | { stock: V2XStock; token?: undefined; onBuy: (stock: V2XStock) => void; onSell: (stock: V2XStock) => void }
);

const TokenDetailScreen: React.FC<TokenDetailScreenProps> = (props) => {
  const { token, stock: initialStock, onBack, onNavigate } = props;
  const [stock, setStock] = useState<V2XStock | undefined>(initialStock);
  const symbol = (initialStock?.symbol ?? token?.symbol ?? '').toUpperCase();
  const displayName = initialStock ? initialStock.name : token?.name || symbol;
  const [period, setPeriod] = useState<V2RateHistoryPeriod>('7d');
  const [seriesByPeriod, setSeriesByPeriod] = useState<Partial<Record<V2RateHistoryPeriod, V2RateHistoryPoint[]>>>({});
  const [latest, setLatest] = useState<V2RateInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    getFavoriteTokenSymbols().then(setFavorites);
  }, []);

  const loadLatest = useCallback(async () => {
    if (initialStock) {
      // Stocks are priced by their own endpoint, which also says whether
      // they can be traded right now.
      const fresh = await v2GetXStock(initialStock.symbol);
      setStock(fresh);
      setLatest(fresh.price_usdt ? { price_usd: fresh.price_usdt, change_24h: fresh.change_24h ?? '0' } : null);
      return;
    }
    const response = await v2GetRates([symbol]);
    setLatest(response.rates[symbol] ?? null);
  }, [initialStock, symbol]);

  const loadHistory = useCallback(async (targetPeriod: V2RateHistoryPeriod) => {
    const response = await v2GetRatesHistory([symbol], targetPeriod);
    const points = response.series[symbol] ?? [];
    setSeriesByPeriod(current => ({ ...current, [targetPeriod]: points }));
    return points;
  }, [symbol]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (seriesByPeriod[period]) { return; }
      setLoading(true);
      setError(null);
      try {
        await loadHistory(period);
      } catch {
        if (active) { setError('Historical data is temporarily unavailable'); }
      } finally {
        if (active) { setLoading(false); }
      }
    };
    load();
    return () => { active = false; };
  }, [loadHistory, period, seriesByPeriod]);

  useEffect(() => {
    loadLatest().catch(() => {
      // Keep the list's stock price rather than blanking it.
      setLatest(initialStock?.price_usdt
        ? { price_usd: initialStock.price_usdt, change_24h: initialStock.change_24h ?? '0' }
        : null);
    });
  }, [initialStock, loadLatest]);

  const points = useMemo(() => seriesByPeriod[period] ?? [], [period, seriesByPeriod]);
  const change = useMemo(() => calculatePeriodChange(points), [points]);
  const historyPrice = points.length > 0 ? Number(points[points.length - 1].price_usd) : 0;
  const currentPrice = Number(latest?.price_usd ?? 0) || historyPrice;
  const chartColor = change.absolute < 0 ? '#E76A68' : '#35B99A';
  const isFavorite = favorites.includes(symbol);

  const onToggleFavorite = useCallback(async () => {
    try {
      setFavorites(await toggleFavoriteTokenSymbol(favorites, symbol));
    } catch {
      Alert.alert('Favorites', 'Could not update Favorites. Please try again.');
    }
  }, [favorites, symbol]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await Promise.all([loadLatest(), loadHistory(period)]);
    } catch {
      setError('Market data is temporarily unavailable');
    } finally {
      setRefreshing(false);
    }
  }, [loadHistory, loadLatest, period]);

  const pausedReason = stock ? tradingPausedReason(stock) : null;
  // The market rate is for reference only, so an old price is shown as such
  // rather than blocking Buy / Sell.
  const ageNote = stock ? priceAgeNote(stock) : null;
  const handleBuy = () => {
    if (pausedReason) { return; }
    if (props.stock) { props.onBuy(stock ?? props.stock); } else { props.onBuy(props.token); }
  };
  const handleSell = () => {
    if (pausedReason) { return; }
    if (props.stock) { props.onSell(stock ?? props.stock); } else { props.onSell(props.token); }
  };
  const dateRange = formatDateRange(points);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.white} />
      <SimpleHeader title={stock ? `${displayName} xStocks` : displayName} onBack={onBack} backgroundVariant="white" />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primaryNavy} />}
      >
        <View style={styles.pairRow}>
          {stock ? (
            <View style={styles.pairLabel}>
              <Text style={styles.stockTicker}>{xStockTicker(stock.symbol)}</Text>
              <Text style={styles.stockName}>{displayName}</Text>
            </View>
          ) : (
            <View style={styles.pairLabel}>
              <View style={[styles.dot, { backgroundColor: chartColor }]} />
              <Text style={styles.pairText}>{displayName} / Tether</Text>
            </View>
          )}
          <TouchableOpacity accessibilityLabel={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'} onPress={onToggleFavorite}>
            <Text style={[styles.favorite, isFavorite && styles.favoriteActive]}>{isFavorite ? '★' : '☆'}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.quoteRow}>
          <Text style={styles.price}>{formatPrice(currentPrice)}</Text>
          {points.length >= 2 && (
            <Text style={[styles.change, { color: chartColor }]}>
              {formatSigned(change.absolute)} ({formatSigned(change.percent)}%)
            </Text>
          )}
        </View>
        <Text style={styles.currency}>USDT</Text>
        {ageNote && <Text style={styles.priceAge}>{ageNote}</Text>}

        <View style={styles.periodTabs}>
          {PERIODS.map(item => (
            <TouchableOpacity
              key={item.value}
              style={[styles.periodTab, period === item.value && styles.activePeriodTab]}
              onPress={() => setPeriod(item.value)}
            >
              <Text style={[styles.periodText, period === item.value && styles.activePeriodText]}>{item.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.chartFrame}>
          {loading ? (
            <ActivityIndicator color={COLORS.primaryNavy} style={styles.chartLoader} />
          ) : (
            <MarketChart points={points} height={220} color={chartColor} />
          )}
          {error && (
            <TouchableOpacity style={styles.retry} onPress={() => {
              setSeriesByPeriod(current => ({ ...current, [period]: undefined }));
            }}>
              <Text style={styles.retryText}>{error} · Retry</Text>
            </TouchableOpacity>
          )}
        </View>
        {dateRange && (
          <View style={styles.dateRow}>
            <Text style={styles.dateText}>{dateRange.start}</Text>
            <Text style={styles.dateText}>{dateRange.end}</Text>
          </View>
        )}

        {pausedReason && <Text style={styles.pausedNote}>{pausedReason}</Text>}

        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.actionButton, styles.buyButton, !!pausedReason && styles.disabledButton]}
            onPress={handleBuy}
            disabled={!!pausedReason}
          >
            <Text style={styles.buyText}>Buy</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, styles.sellButton, !!pausedReason && styles.disabledButton]}
            onPress={handleSell}
            disabled={!!pausedReason}
          >
            <Text style={styles.sellText}>Sell</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

const formatPrice = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) { return 'N/A'; }
  const digits = value >= 1000 ? 2 : value >= 1 ? 4 : 6;
  const [whole, decimal] = value.toFixed(digits).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${decimal}`;
};

const formatSigned = (value: number) => {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};

const formatDateRange = (points: V2RateHistoryPoint[]) => {
  if (points.length === 0) { return null; }
  const format = (raw: string) => {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) { return ''; }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getUTCDate()} ${months[date.getUTCMonth()]}`;
  };
  return { start: format(points[0].timestamp), end: format(points[points.length - 1].timestamp) };
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.white },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 20, paddingBottom: 120 },
  pairRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },
  pairLabel: { flexDirection: 'row', alignItems: 'center' },
  dot: { width: 6, height: 6, borderRadius: 3, marginRight: 6 },
  pairText: { color: COLORS.primaryNavy, fontWeight: '600', fontSize: 14 },
  stockTicker: { color: COLORS.primaryNavy, fontWeight: '600', fontSize: 12, marginRight: 6 },
  stockName: { color: COLORS.blueSteel, fontSize: 10 },
  priceAge: { color: COLORS.blueSteel, fontSize: 12, lineHeight: 17, marginTop: 4 },
  pausedNote: { color: COLORS.brandOrange, fontSize: 12, lineHeight: 17, marginTop: 16 },
  disabledButton: { opacity: 0.45 },
  favorite: { color: COLORS.primaryNavy, fontSize: 28, lineHeight: 30 },
  favoriteActive: { color: '#D6A72C' },
  quoteRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 10 },
  price: { color: COLORS.primaryNavy, fontSize: 28, fontWeight: '500' },
  change: { fontSize: 13, fontWeight: '600' },
  currency: { color: COLORS.blueSteel, fontSize: 11, marginTop: -2 },
  periodTabs: { height: 34, flexDirection: 'row', backgroundColor: COLORS.pureLight, borderRadius: 7, marginTop: 16, padding: 2 },
  periodTab: { flex: 1, alignItems: 'center', justifyContent: 'center', borderRadius: 5 },
  activePeriodTab: { backgroundColor: COLORS.white, borderWidth: 1, borderColor: COLORS.borderMuted },
  periodText: { color: COLORS.blueSteel, fontSize: 11 },
  activePeriodText: { color: COLORS.primaryNavy, fontWeight: '600' },
  chartFrame: { minHeight: 220, marginTop: 14, justifyContent: 'center' },
  chartLoader: { marginVertical: 90 },
  retry: { position: 'absolute', alignSelf: 'center', bottom: 8, padding: 6 },
  retryText: { color: COLORS.brandOrange, fontSize: 12 },
  dateRow: { flexDirection: 'row', justifyContent: 'space-between', paddingRight: 52, marginTop: 2 },
  dateText: { color: COLORS.blueSteel, fontSize: 10 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 24 },
  actionButton: { flex: 1, height: 44, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  buyButton: { backgroundColor: COLORS.primaryNavy },
  sellButton: { backgroundColor: COLORS.white, borderWidth: 1, borderColor: COLORS.primaryNavy },
  buyText: { color: COLORS.white, fontSize: 14, fontWeight: '500' },
  sellText: { color: COLORS.primaryNavy, fontSize: 14, fontWeight: '500' },
});

export default TokenDetailScreen;
